import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { APP_VERSION } from '../../../constants/app.constant';
import { ILoginAPIResponse, ILoginData, IRefreshTokenAPIResponse, ISignupData } from '../../../models/auth.model';
import { BaseApiService } from '../../base-api/base-api.service';
import { StorageService } from '../../storage/storage.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService extends BaseApiService {
    protected override endPoint = '';

    constructor(
        protected override http: HttpClient,
        private storageService: StorageService,
    ) {
        super(http);
    }

    firstTimeLogin(token: string): Observable<ILoginAPIResponse> {
        const payload: ILoginData = {
            token,
            type: 'email',
            deviceType: 'web',
            deviceId: this.storageService.getDeviceId() as string,
            appVersion: APP_VERSION,
            zoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        return this.http.post<ILoginAPIResponse>(`${this.fullPath}/web/first_login`, payload).pipe(
            tap((response) => {
                this.storageService.setLoginUser(response);
            }),
        );
    }

    login(data: ILoginData): Observable<ILoginAPIResponse> {
        return this.http.post<ILoginAPIResponse>(`${this.fullPath}/login`, data);
    }

    refreshToken(): Observable<IRefreshTokenAPIResponse> {
        const refreshToken = this.storageService.getRefreshToken();

        return this.http
            .post<IRefreshTokenAPIResponse>(
                `${this.fullPath}/token/refresh`,
                {},
                {
                    params: { noToken: true },
                    headers: {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${refreshToken}`,
                    },
                },
            )
            .pipe(
                tap((res) => {
                    this.storageService.updateToken(res.accessToken, res.refreshToken);
                }),
            );
    }

    signup(data: ISignupData): Observable<void> {
        const body = {
            email: data.email,
            password: data.password,
            name: data.name,
            zoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        return this.http.post<void>(`${this.fullPath}/register`, body);
    }

    logout(): Observable<void> {
        const body = {
            deviceType: 'web',
            deviceId: this.storageService.getDeviceId() as string,
        };

        return this.http.post<void>(`${this.fullPath}/logout`, body);
    }

    resendVerifyEmail(email: string): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/email/verify/resend`, { email });
    }
}
